"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { usePreferredLanguageStore } from "@/stores/preferred-language-store";
import { defaultLocale, locales, isRtlLocale, type Locale } from "@/i18n/config";
import { detectDeviceLanguage } from "@/lib/i18n/device-language";

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => Promise<void>;
  preloadLocale: (locale: Locale) => Promise<void>;
  translationVersion: number; // Force re-render when translations load
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const LOCALE_COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year

function setLocaleCookie(locale: Locale) {
  if (typeof document === "undefined") return;
  document.cookie = `locale=${locale}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax`;
}

/**
 * Get the resolved locale from storage sources.
 * Priority: 1. localStorage (user's explicit choice), 2. Profile, 3. Device detection.
 * localStorage must win over profile because profile refetches can return stale DB data
 * (e.g. before update-language API completes), which was overwriting the user's selection.
 */
function getStoredLocale(profilePreferredLanguage: string | null | undefined): Locale | null {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem("locale");
  if (stored && locales.includes(stored as Locale)) {
    return stored as Locale;
  }
  if (profilePreferredLanguage && locales.includes(profilePreferredLanguage as Locale)) {
    return profilePreferredLanguage as Locale;
  }
  return null;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  // This provider sits at the ROOT of every page, the website included, and the only thing it
  // needs from the profile is the preferred language. It used to call useProfile(), which
  // subscribes to the whole profile store — including `otherProfiles`, an array of up to 120 full
  // profile objects — so every Discover load re-rendered the application from the root down.
  //
  // Reading one field off that store fixed the re-renders but not the weight: importing the
  // profile store pulls in the profile API client, the authenticated fetch wrapper and the
  // Supabase browser client, so somebody reading the cookie policy downloaded all of it. The
  // field is now mirrored into a store that imports nothing. See stores/preferred-language-store.ts.
  const preferredLanguage = usePreferredLanguageStore((s) => s.preferredLanguage);

  // Initial state: avoid flicker by detecting device language synchronously when no preference exists
  // Priority: localStorage (saved preference) → device language → English fallback
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === "undefined") return defaultLocale;
    const stored = localStorage.getItem("locale");
    if (stored && locales.includes(stored as Locale)) return stored as Locale;
    return detectDeviceLanguage();
  });
  const [translationVersion, setTranslationVersion] = useState(0); // Force re-render when translations load

  const preloadLocale = async (newLocale: Locale, forceReload: boolean = false): Promise<void> => {
    if (typeof window === "undefined") return;
    
    try {
      const { preloadTranslation } = await import("@/lib/i18n/translations");
      await preloadTranslation(newLocale, forceReload);
      // Translation loaded successfully - it's now in the cache
      // Increment version to force components to re-render
      setTranslationVersion(prev => prev + 1);
    } catch (error) {
      // Translation file doesn't exist - this is expected for languages without translations
      // Will fall back to English automatically, so we don't need to do anything here
      // Silently continue - English fallback will work
      if (process.env.NODE_ENV === "development") {
        console.log(`[i18n] Could not preload ${newLocale}, will use English fallback`);
      }
      // Still increment version to trigger re-render (will use English fallback)
      setTranslationVersion(prev => prev + 1);
    }
  };

  const setLocale = async (newLocale: Locale) => {
    // Force reload translation when switching languages to ensure fresh data
    const forceReload = newLocale !== locale;

    // Preload translation before switching
    await preloadLocale(newLocale, forceReload);

    setLocaleState(newLocale);
    setTranslationVersion((prev) => prev + 1);
    if (typeof window !== "undefined") {
      localStorage.setItem("locale", newLocale);
      setLocaleCookie(newLocale);
    }
  };

  useEffect(() => {
    // Priority: 1. localStorage (user's explicit choice), 2. Profile, 3. Device language
    // localStorage MUST win over profile - profile refetches can return stale DB data.
    const initializeLocale = async () => {
      const storedOrProfile = getStoredLocale(preferredLanguage);
      const resolvedLocale: Locale = storedOrProfile ?? detectDeviceLanguage();

      await preloadLocale(resolvedLocale);
      setLocaleState(resolvedLocale);

      if (typeof window !== "undefined") {
        localStorage.setItem("locale", resolvedLocale);
        setLocaleCookie(resolvedLocale);
      }
    };

    initializeLocale();
  }, [preferredLanguage]);

  // Update HTML lang/dir attributes
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
      document.documentElement.dir = isRtlLocale(locale) ? "rtl" : "ltr";
    }
  }, [locale]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, preloadLocale, translationVersion }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
}

